import { createEffect, createEvent, createStore, sample } from 'effector';
import { clientGetOrgWorkspaceMembers, clientGetWorkspaceMembers } from '@nx-frontend/shared/api/webnotes/client';
import { $members } from '@nx-frontend/shared/store/members';
import { upsertWorkspaceMembersEvent } from '@nx-frontend/shared/store/workspace-members';
import { WorkspaceMemberDto, workspaceMembersFromDto } from '@nx-frontend/shared/types';

interface WorkspaceMembersState {
  entities: Record<string, WorkspaceMemberDto[]>;
  fetched: boolean;
  isFetching: boolean;
}

export const initialWorkspaceMembersState: WorkspaceMembersState = {
  entities: {},
  fetched: false,
  isFetching: false,
};

export const $workspaceMembers = createStore<WorkspaceMembersState>(initialWorkspaceMembersState);

export const fetchOrgWorkspaceMembersEvent = createEvent<string>(); // orgId
export const fetchWorkspaceMembersEvent = createEvent<string>(); // workspaceId;
export const startFetchOrgWorkspaceMembersEvent = createEvent();
export const startFetchWorkspaceMembersEvent = createEvent();

export const fetchOrgWorkspaceMembersFx = createEffect(async (orgId: string) => {
  return await clientGetOrgWorkspaceMembers(orgId);
});

export const fetchWorkspaceMembersFx = createEffect(async (workspaceId: string) => {
  return await clientGetWorkspaceMembers(workspaceId);
});

$workspaceMembers
  .on(fetchOrgWorkspaceMembersFx.doneData, (_, entities) => {
    return {
      ..._,
      entities,
      fetched: true,
      isFetching: false,
    };
  })
  .on(fetchWorkspaceMembersFx.doneData, (state, members) => {
    const entities = { ...state.entities };
    const workspaceId = members.length ? members[0]?.workspaceId : '';

    if (!workspaceId) return state;

    const workspaceMemebers = entities[workspaceId] || [];
    const entityMembers = [...workspaceMemebers];

    members.forEach((member) => {
      const index = entityMembers.findIndex((item) => item.globalId === member.globalId);
      if (index !== -1) {
        entityMembers[index] = member;
      } else {
        entityMembers.push(member);
      }
    });

    return {
      ...state,
      entities: {
        ...entities,
        [workspaceId]: entityMembers,
      },
      fetched: true,
      isFetching: false,
    };
  });

$workspaceMembers
  .on(startFetchOrgWorkspaceMembersEvent, (_) => {
    return {
      ..._,
      isFetching: true,
    };
  })
  .on(startFetchWorkspaceMembersEvent, (_) => {
    return {
      ..._,
      isFetching: true,
    };
  });

sample({
  source: { workspaceMembers: $workspaceMembers },
  clock: fetchOrgWorkspaceMembersEvent,
  filter: ({ workspaceMembers }) => workspaceMembers.fetched === false && workspaceMembers.isFetching === false,
  fn: (_, orgId) => {
    return orgId;
  },
  target: [startFetchOrgWorkspaceMembersEvent, fetchOrgWorkspaceMembersFx],
});

sample({
  source: { workspaceMembers: $workspaceMembers },
  clock: fetchWorkspaceMembersEvent,
  filter: ({ workspaceMembers }) => workspaceMembers.isFetching === false,
  fn: (_, workspaceId) => {
    return workspaceId;
  },
  target: [startFetchWorkspaceMembersEvent, fetchWorkspaceMembersFx],
});

sample({
  source: $members,
  clock: fetchWorkspaceMembersFx.doneData,
  fn: (_, members) => {
    return workspaceMembersFromDto(members);
  },
  target: [upsertWorkspaceMembersEvent],
});
